import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Box, Typography, Divider } from '@mui/material';
import { logout } from '../../../utils/auth/session';
import { NAVBAR_MENU_KEYS, NS } from '../../../i18n/constants/keys';
import { translateRole } from '../../../i18n/helpers/translateRole';
import { getImageSrc } from '../../../utils/helpers/image-utils';
import {
  ChevronRightIcon,
  LockKeyholeIcon,
  LogoutIcon,
  UserIcon,
} from '../../../icons/animated';
import ViewOption from '../ViewOption';

const MotionBox = motion(Box);

const SPRING = {
  type: 'spring',
  stiffness: 400,
  damping: 32,
  mass: 0.8,
};

const contentAnimations = {
  initial: { opacity: 0, filter: 'blur(8px)', y: 4 },
  animate: {
    opacity: 1,
    filter: 'blur(0px)',
    y: 0,
    transition: { delay: 0.12, duration: 0.25 },
  },
  exit: {
    opacity: 0,
    filter: 'blur(6px)',
    y: 4,
    transition: { duration: 0.1 },
  },
};

interface UserData {
  name: string;
  avatar: string;
  initials: string;
  role: string;
  email: string;
}

const Avatar: React.FC<{
  large?: boolean;
  user: UserData;
}> = ({
  large = false,
  user,
}) => {
  const hasAvatar =
    Boolean(user.avatar) &&
    user.avatar !== 'null' &&
    user.avatar !== 'undefined' &&
    user.avatar.trim() !== '';

  return (
    <MotionBox
      layoutId="hiacuity-user-avatar"
      transition={SPRING}
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        userSelect: 'none',
        width: large ? 64 : 40,
        height: large ? 64 : 40,
        fontSize: large ? '1.125rem' : '0.875rem',
        fontWeight: large ? 700 : 600,
        background: 'linear-gradient(135deg, #059669, #047857, #115e59)',
        color: '#ffffff',
        boxShadow: large
          ? '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
          : 'inset 0 2px 4px rgba(0,0,0,0.1)',
        border: '2px solid rgba(255, 255, 255, 0.7)',
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      {hasAvatar ? (
        <Box
          component="img"
          src={getImageSrc(user.avatar, '')}
          alt={user.name}
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <Typography component="span" sx={{ fontSize: large ? '1.125rem' : '0.875rem', fontWeight: 'bold' }}>
          {user.initials}
        </Typography>
      )}
      {/* Online indicator */}
      <Box
        component="span"
        sx={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: large ? 14 : 10,
          height: large ? 14 : 10,
          border: large ? '2px solid #ffffff' : '1px solid #ffffff',
          bgcolor: '#34d399',
          borderRadius: '50%',
        }}
      />
    </MotionBox>
  );
};

const MenuItem: React.FC<{
  icon: React.ReactNode;
  title: string;
  description?: string;
  onClick: () => void;
  isDestructive?: boolean;
}> = ({
  icon,
  title,
  description,
  onClick,
  isDestructive = false,
}) => {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 1.5,
        py: 1,
        borderRadius: '12px',
        cursor: 'pointer',
        userSelect: 'none',
        transition: 'all 0.15s ease',
        color: isDestructive ? '#dc2626' : '#1e293b',
        '&:hover': {
          bgcolor: isDestructive ? 'rgba(254, 242, 242, 0.8)' : '#f8fafc',
          '& .action-icon': {
            bgcolor: isDestructive ? '#fee2e2' : '#ecfdf5',
            color: isDestructive ? '#dc2626' : '#059669',
          },
        },
        '&:active': {
          bgcolor: isDestructive ? '#fee2e2' : '#f1f5f9',
        },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: 32,
          height: 32,
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'colors 0.2s',
          bgcolor: isDestructive ? 'rgba(254, 226, 226, 0.7)' : '#f1f5f9',
          color: isDestructive ? '#dc2626' : '#475569',
        }}
      >
        {icon}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
        <Typography
          noWrap
          sx={{
            fontSize: '0.75rem',
            fontWeight: 600,
            color: isDestructive ? '#dc2626' : '#1e293b',
          }}
        >
          {title}
        </Typography>
        {description && (
          <Typography
            noWrap
            sx={{
              fontSize: '11px',
              mt: 0.25,
              color: isDestructive ? '#f87171' : '#94a3b8',
            }}
          >
            {description}
          </Typography>
        )}
      </Box>

      {!isDestructive && (
        <Box sx={{ flexShrink: 0, color: '#cbd5e1', transition: 'all 0.2s' }}>
          <ChevronRightIcon size={14} />
        </Box>
      )}
    </Box>
  );
};

const MenuContent: React.FC<{
  user: UserData;
  onClose: () => void;
  onSignOut: () => void;
}> = ({
  user,
  onClose,
  onSignOut,
}) => {
  const { t } = useTranslation(NS.navbarMenus);
  const history = useHistory();

  return (
    <MotionBox
      layoutId="hiacuity-user-btn"
      transition={SPRING}
      sx={{
        borderRadius: '20px',
        position: 'absolute',
        top: 0,
        right: 0,
        zIndex: 9999,
        width: { xs: 288, sm: 320 },
        bgcolor: '#f8fafc',
        border: '1px solid rgba(226, 232, 240, 0.9)',
        boxShadow: '0 20px 50px rgba(0,0,0,0.16)',
        backdropFilter: 'blur(20px)',
        overflow: 'hidden',
        p: 1,
        color: '#1e293b',
      }}
    >
      {/* Header section with large avatar */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pt: 1.5, pb: 1.5, px: 1 }}>
        <Box sx={{ mb: 1 }}>
          <Avatar large user={user} />
        </Box>

        <MotionBox
          sx={{ textAlign: 'center', width: '100%', px: 1 }}
          initial={contentAnimations.initial}
          animate={contentAnimations.animate}
          exit={contentAnimations.exit}
        >
          <Typography noWrap sx={{ fontSize: '0.875rem', fontWeight: 'bold', color: '#0f172a' }}>
            {user.name}
          </Typography>
          <Typography noWrap sx={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 500, mt: 0.25 }}>
            {user.email}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 1 }}>
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                px: 1,
                py: 0.25,
                borderRadius: '9999px',
                fontSize: '11px',
                fontWeight: 600,
                bgcolor: 'rgba(209, 250, 229, 0.8)',
                color: '#065f46',
                border: '1px solid rgba(167, 243, 208, 0.6)',
              }}
            >
              {translateRole(t, user.role)}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '11px', fontWeight: 500, color: '#047857' }}>
              <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#10b981' }} />
              <Typography component="span" sx={{ fontSize: '11px', fontWeight: 500, color: '#047857' }}>
                {t(NAVBAR_MENU_KEYS.profile.online)}
              </Typography>
            </Box>
          </Box>
        </MotionBox>
      </Box>

      {/* Quick Actions (View Switcher) */}
      <MotionBox
        sx={{ px: 0.75, pb: 1 }}
        initial={contentAnimations.initial}
        animate={contentAnimations.animate}
        exit={contentAnimations.exit}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 0.5, mb: 0.5 }}>
          <Typography sx={{ fontSize: '10px', fontWeight: 'bold', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {t(NAVBAR_MENU_KEYS.profile.quickActions)}
          </Typography>
        </Box>
        <ViewOption />
      </MotionBox>

      {/* Menu items inner card */}
      <MotionBox
        sx={{
          bgcolor: '#ffffff',
          borderRadius: '16px',
          p: 0.75,
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
          border: '1px solid rgba(226, 232, 240, 0.7)',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.25,
        }}
        initial={contentAnimations.initial}
        animate={contentAnimations.animate}
        exit={contentAnimations.exit}
      >
        <MenuItem
          icon={<UserIcon size={16} />}
          title={t(NAVBAR_MENU_KEYS.profile.items.profileSettings.title)}
          description={t(
            NAVBAR_MENU_KEYS.profile.items.profileSettings.description,
          )}
          onClick={() => {
            onClose();
            history.push('/user-profile');
          }}
        />

        <MenuItem
          icon={<LockKeyholeIcon size={16} />}
          title={t(NAVBAR_MENU_KEYS.profile.items.changePassword.title)}
          description={t(
            NAVBAR_MENU_KEYS.profile.items.changePassword.description,
          )}
          onClick={() => {
            onClose();
            history.push('/change-password');
          }}
        />

        <Divider sx={{ my: 0.5, borderColor: '#f1f5f9' }} />

        <MenuItem
          isDestructive
          icon={<LogoutIcon size={16} />}
          title={t(NAVBAR_MENU_KEYS.profile.signOut.title)}
          description={t(NAVBAR_MENU_KEYS.profile.signOut.description)}
          onClick={() => {
            onClose();
            onSignOut();
          }}
        />
      </MotionBox>

      {/* HiAcuity Footer Credit */}
      <MotionBox
        sx={{
          pt: 1.25,
          pb: 0.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.5,
        }}
        initial={contentAnimations.initial}
        animate={contentAnimations.animate}
        exit={contentAnimations.exit}
      >
        <Typography sx={{ fontSize: '11px', fontWeight: 500, color: '#94a3b8' }}>
          HiAcuity Workspace
        </Typography>
      </MotionBox>
    </MotionBox>
  );
};

export const Profile: React.FC = () => {
  const { t } = useTranslation(NS.navbarMenus);
  const history = useHistory();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Retrieve user details from localStorage with reactive state
  const [profilePic, setProfilePic] = useState<string>(() => {
    const raw = localStorage.getItem('profilePic') || '';
    return !raw || raw === 'null' || raw === 'undefined' || !raw.trim() ? '' : raw.trim();
  });
  const [firstName, setFirstName] = useState<string>(() => localStorage.getItem('firstname') || '');
  const [lastName, setLastName] = useState<string>(() => localStorage.getItem('lastname') || '');
  const [email, setEmail] = useState<string>(() => localStorage.getItem('email') || 'user@example.com');
  const role = localStorage.getItem('role') || 'Candidate';

  useEffect(() => {
    const handleProfileUpdate = (e?: Event) => {
      const customEvent = e as CustomEvent<{ profilePic?: string; firstName?: string; lastName?: string; email?: string }>;
      const newPic = customEvent?.detail?.profilePic ?? (localStorage.getItem('profilePic') || '');
      const cleanPic = !newPic || newPic === 'null' || newPic === 'undefined' || !newPic.trim() ? '' : newPic.trim();
      setProfilePic(cleanPic);

      const fn = customEvent?.detail?.firstName ?? (localStorage.getItem('firstname') || '');
      const ln = customEvent?.detail?.lastName ?? (localStorage.getItem('lastname') || '');
      const em = customEvent?.detail?.email ?? (localStorage.getItem('email') || 'user@example.com');
      setFirstName(fn);
      setLastName(ln);
      setEmail(em);
    };

    window.addEventListener('profilePicUpdated' as any, handleProfileUpdate);
    window.addEventListener('userProfileUpdated' as any, handleProfileUpdate);
    window.addEventListener('storage', handleProfileUpdate);

    return () => {
      window.removeEventListener('profilePicUpdated' as any, handleProfileUpdate);
      window.removeEventListener('userProfileUpdated' as any, handleProfileUpdate);
      window.removeEventListener('storage', handleProfileUpdate);
    };
  }, []);

  const user: UserData = {
    name:
      `${firstName} ${lastName}`.trim() ||
      t(NAVBAR_MENU_KEYS.profile.defaultUserName),
    avatar: profilePic,
    initials:
      `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U',
    role: role || 'User',
    email,
  };

  // Close on escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Logout function
  const logOut = async () => {
    await logout();
    history.push('/login');
  };

  return (
    <Box sx={{ position: 'relative', width: 40, height: 40 }} ref={rootRef}>
      <AnimatePresence>
        {!open ? (
          <MotionBox
            component="button"
            key="closed"
            layoutId="hiacuity-user-btn"
            transition={SPRING}
            onClick={() => setOpen(true)}
            aria-label="Open profile menu"
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: 40,
              height: 40,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              border: 0,
              p: 0,
              overflow: 'hidden',
              bgcolor: 'transparent',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              transition: 'transform 0.15s ease',
              '&:hover': {
                transform: 'scale(1.05)',
              },
              '&:focus': {
                outline: 'none',
              },
            }}
          >
            <Avatar user={user} />
          </MotionBox>
        ) : (
          <MenuContent
            key="open"
            user={user}
            onClose={() => setOpen(false)}
            onSignOut={logOut}
          />
        )}
      </AnimatePresence>
    </Box>
  );
};

export default Profile;
